<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { Popup } from "vant";
import { useGlobalUser } from "@/stores/globalUser";
import { useI18n } from "vue-i18n";
import { CoinBalanceRecordEntityType } from "@/types/coin";
import { useMate } from "@/stores/mate";
import NormalTicketVote from "@/components/mobile/coin/coin-vote/NormalTicketVote.vue";
import {
  VoteTargetEntityType,
  VoteTargetType,
} from "@/composable/rank/rankConfig";
import { goLoginIfNot } from "@/use/go";
import { Mate } from "@/types/entity";

const props = withDefaults(
  defineProps<{
    type: VoteTargetEntityType;
    item: VoteTargetType;
    btnSize?: "xs" | "sm" | "md" | "lg";
    teleport?: string;
  }>(),
  {
    btnSize: "xs",
  }
);
const emit = defineEmits<{
  (e: "onVoted"): void;
}>();
// Constants & Stores
const user = useGlobalUser();
const mateStore = useMate();
const { t } = useI18n();
// States
const show = ref(false);
const shouldShow = computed(() => {
  const mateList: Mate[] = [];
  if (props.type === CoinBalanceRecordEntityType.Mate) {
    mateList.push(props.item as Mate);
  } else if (props.type === CoinBalanceRecordEntityType.Collection) {
    if (!(props.item as Collection).participantzz) {
      return false;
    }
    mateList.push(...(props.item as Collection).participantzz!);
  } else {
    mateList.push((props.item as StoryBook).mate);
  }
  return !mateList.some((mate) => mateStore.isMyMate(mate?.uuid));
});
const refreshing = ref(false);
const ticketType = ref<0 | 1>(0);
const ticketTypeConfig: { label: string; value: 0 | 1 }[] = [
  { label: t("Coin.regularTicket"), value: 0 },
  { label: t("Coin.goldTicket"), value: 1 },
];
const shouldShowTicketSwitch = computed(() => {
  return !refreshing.value && user.isReviewer;
});
// Lifecycle
watch(show, async (show) => {
  if (show) {
    refreshing.value = true;
    try {
      ticketType.value = 0;
      await user.refreshUser();
    } finally {
      refreshing.value = false;
    }
  }
});

const onVoted = async () => {
  emit("onVoted");
  show.value = false;
};
const onClick = async () => {
  if (!(await goLoginIfNot())) return;
  show.value = true;
};
</script>
<template>
  <div v-if="shouldShow">
    <div @click.stop="onClick">
      <slot>
        <button :class="`btn btn-${btnSize} btn-neutral  rounded`">
          <i :class="`iconify mdi--ticket text-${btnSize}`" />
        </button>
      </slot>
    </div>
    <popup
      v-model:show="show"
      :close-on-click-overlay="false"
      :teleport="teleport"
      class="w-[90%] max-w-[476px] max-h-[90%] p-4 md:px-6 overflow-y-auto"
      closeable
      lazy-render
      position="center"
      round
    >
      <div
        class="flex flex-col justify-between gap-2 w-full h-full overflow-hidden text-base-content"
      >
        <div class="flex flex-col gap-1 flex-1">
          <div class="flex flex-col gap-2 flex-1 mb-3">
            <div class="font-bold text-xl">{{ $t("Coin.voteIncentive") }}</div>
            <story-vote-card
              v-if="
                type === CoinBalanceRecordEntityType.Story ||
                type === CoinBalanceRecordEntityType.StoryBook
              "
              :story-book="item as StoryBook"
              text="howManyTicketsToVote"
            />
            <game-vote-card
              v-else-if="type === CoinBalanceRecordEntityType.Game"
              :game="item as GalGame"
              text="howManyTicketsToVote"
            />
            <CollectionVoteCard
              v-else-if="type === CoinBalanceRecordEntityType.Collection"
              :collection="item as Collection"
              text="howManyTicketsToVote"
            />
            <mate-vote-card
              v-else
              :mate="item as Mate"
              text="howManyTicketsToVote"
            />
          </div>
          <div class="flex items-center justify-between">
            <!--  选择金票/普通票  -->
            <div v-if="shouldShowTicketSwitch" class="flex items-center gap-5">
              <div
                v-for="(item, idx) in ticketTypeConfig"
                :key="idx"
                class="flex items-center gap-2"
              >
                <input
                  :checked="ticketType === item.value"
                  :class="
                    ticketType === item.value
                      ? 'radio-primary'
                      : 'radio-secondary'
                  "
                  :value="item.value"
                  class="radio radio-sm"
                  type="radio"
                  @change="ticketType = item.value"
                />
                <span class="text-base-content">
                  {{ item.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
        <normal-ticket-vote
          v-if="ticketType === 0"
          :item="item"
          :loading="refreshing"
          :type="type"
          @onVoted="onVoted"
        />
        <gold-ticket-vote
          v-else-if="ticketType === 1"
          :item="item"
          :loading="refreshing"
          :type="type"
          @onVoted="onVoted"
        />
      </div>
    </popup>
  </div>
</template>
